import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Star,
  ShieldCheck,
  Truck,
  Headphones,
  CheckCircle2,
  Mail,
  ZoomIn,
  X,
  Play,
} from 'lucide-react';
import { toast } from 'sonner';
import { priceLabel } from '../mock';
import { useCatalog } from '../hooks/useCatalog';

const API = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith('/api/') ? `${API}${u}` : u);
const isVideo = (u) => /\.(mp4|webm|mov)(\?|$)/i.test(u || '');

export default function ProductDetail() {
  const { id } = useParams();
  const { products, categoryTree, findSubcategory } = useCatalog();
  const product = useMemo(() => products.find((p) => p.id === id), [id, products]);
  const [tab, setTab] = useState('specs');
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => setActiveImg(0), [id]);
  useEffect(() => setZoomed(false), [activeImg, lightboxOpen]);
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen]);

  if (!product) {
    return (
      <div className="pt-[120px] min-h-[60vh] max-w-[1400px] mx-auto px-6 text-center">
        <h1 className="font-display text-4xl font-bold text-neutral-900 mb-3">
          Produk tidak ditemukan
        </h1>
        <p className="text-neutral-600 mb-8">
          Produk yang Anda cari mungkin sudah dipindahkan atau dihapus.
        </p>
        <Link
          to="/products"
          className="inline-flex items-center gap-2 bg-px-red hover:bg-red-700 text-white px-7 py-3 rounded-full font-semibold"
        >
          Kembali ke Katalog
        </Link>
      </div>
    );
  }

  const gallery = product.images && product.images.length > 0
    ? product.images
    : [...new Set([product.image, product.diagram_image].filter(Boolean))];
  const parent = categoryTree.find((c) => c.slug === product.category);
  const sub = product.subcategory
    ? findSubcategory(product.category, product.subcategory)
    : null;
  const related = products
    .filter((p) => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  const requestQuote = () => {
    toast.success(
      `Permintaan penawaran untuk ${product.name} telah dikirim. Tim sales kami akan segera menghubungi Anda.`
    );
  };

  return (
    <div data-testid="page-product-detail" className="pt-[68px] md:pt-[100px] bg-white">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-8">
        <nav className="text-sm text-neutral-500 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-px-red">Home</Link>
          <span>/</span>
          <Link to="/products" className="hover:text-px-red">Produk</Link>
          {parent && (
            <>
              <span>/</span>
              <Link
                to={`/products?category=${parent.slug}`}
                className="hover:text-px-red"
              >
                {parent.title}
              </Link>
            </>
          )}
          {sub && (
            <>
              <span>/</span>
              <Link
                to={`/products?category=${parent.slug}&sub=${sub.slug}`}
                className="hover:text-px-red"
              >
                {sub.title}
              </Link>
            </>
          )}
          <span>/</span>
          <span className="text-neutral-900 font-medium truncate">{product.name}</span>
        </nav>
      </div>

      {/* Main */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Image gallery — Jilong-style: thumbnails column + big main image */}
        <div>
          <Link
            to="/products"
            className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-px-red mb-5"
          >
            <ChevronLeft className="w-4 h-4" /> Kembali ke katalog
          </Link>
          <div className="flex gap-3 md:gap-4">
            {gallery.length > 1 && (
              <div
                data-testid="gallery-thumbs"
                className="flex flex-col gap-2 md:gap-3 w-16 md:w-20 shrink-0 max-h-[560px] overflow-y-auto pr-1"
              >
                {gallery.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`gallery-thumb-${i}`}
                    className={`relative aspect-square rounded-xl overflow-hidden bg-white border-2 transition-all shrink-0 select-none ${
                      activeImg === i
                        ? 'border-px-red ring-2 ring-red-100'
                        : 'border-neutral-200 hover:border-neutral-400'
                    }`}
                    onContextMenu={(e) => e.preventDefault()}
                    aria-label={`Gambar ${i + 1}`}
                  >
                    {isVideo(src) ? (
                      <div className="relative w-full h-full bg-neutral-900">
                        {product.video_poster ? (
                          <img
                            src={absUrl(product.video_poster)}
                            alt="Video"
                            draggable={false}
                            className="w-full h-full object-cover pointer-events-none"
                          />
                        ) : (
                          <video
                            src={`${src}#t=1`}
                            muted
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-cover pointer-events-none"
                          />
                        )}
                        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="w-7 h-7 rounded-full bg-px-red/90 flex items-center justify-center">
                            <Play className="w-3.5 h-3.5 text-white fill-white ml-0.5" />
                          </span>
                        </span>
                      </div>
                    ) : (
                    <img
                      src={absUrl(src)}
                      alt={`${product.name} ${i + 1}`}
                      draggable={false}
                      className="w-full h-full object-contain p-1 pointer-events-none"
                      style={{ WebkitUserDrag: 'none', userSelect: 'none' }}
                    />
                    )}
                    <span className="absolute bottom-0.5 left-0.5 z-10 text-[7px] font-bold tracking-widest text-white/80 bg-black/35 px-1 rounded pointer-events-none select-none">
                      powerx
                    </span>
                  </button>
                ))}
              </div>
            )}
            {isVideo(gallery[activeImg]) ? (
              <div
                className="relative flex-1 aspect-square rounded-3xl overflow-hidden bg-black border border-neutral-200 flex items-center justify-center"
                onContextMenu={(e) => e.preventDefault()}
                data-testid="main-video-frame"
              >
                <video
                  key={gallery[activeImg]}
                  src={gallery[activeImg]}
                  poster={absUrl(product.video_poster) || undefined}
                  controls
                  playsInline
                  preload="metadata"
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  className="w-full h-full object-contain"
                  data-testid="product-gallery-video"
                />
                <span className="absolute top-4 left-5 z-10 text-[10px] font-bold tracking-[0.2em] text-white/80 bg-black/35 px-1.5 py-0.5 rounded pointer-events-none select-none">
                  powerx
                </span>
                <span className="absolute top-4 right-5 z-10 text-xs text-white/90 bg-black/40 px-2.5 py-1 rounded-full pointer-events-none">
                  {activeImg + 1} / {gallery.length}
                </span>
              </div>
            ) : (
            <div
              className="relative flex-1 aspect-square rounded-3xl overflow-hidden bg-white border border-neutral-200 cursor-zoom-in group/mainimg select-none"
              onClick={() => setLightboxOpen(true)}
              onContextMenu={(e) => e.preventDefault()}
              data-testid="main-image-zoom"
            >
              {product.badge && (
                <span className="absolute top-5 left-5 z-10 bg-px-red text-white text-xs font-bold tracking-wider px-3 py-1.5 rounded-full">
                  {product.badge}
                </span>
              )}
              <span className="absolute top-5 right-5 z-10 inline-flex items-center gap-1.5 text-xs text-neutral-600 bg-white/90 backdrop-blur px-2.5 py-1.5 rounded-full border border-neutral-200 opacity-0 group-hover/mainimg:opacity-100 transition-opacity">
                <ZoomIn className="w-3.5 h-3.5" /> Klik untuk perbesar
              </span>
              {gallery.length > 1 && (
                <span className="absolute bottom-4 right-5 z-10 text-xs text-neutral-500 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full border border-neutral-200">
                  {activeImg + 1} / {gallery.length}
                </span>
              )}
              <span
                className="absolute bottom-4 left-5 z-10 text-[10px] font-bold tracking-[0.2em] text-white/80 bg-black/35 px-1.5 py-0.5 rounded pointer-events-none select-none"
                data-testid="product-image-watermark"
              >
                powerx
              </span>
              <img
                src={absUrl(gallery[activeImg] || product.image)}
                alt={product.name}
                draggable={false}
                className="w-full h-full object-contain p-6 md:p-10 pointer-events-none"
                style={{ WebkitUserDrag: 'none', userSelect: 'none' }}
              />
            </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div>
          <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
            {product.series.toUpperCase()}
          </p>
          <h1
            data-testid="product-detail-name"
            className="font-display text-4xl md:text-5xl font-bold text-neutral-900 leading-tight mb-4"
          >
            {product.name}
          </h1>
          <p className="text-neutral-600 leading-relaxed mb-5">{product.short}</p>

          <div className="flex items-center gap-5 mb-7 text-sm flex-wrap">
            <span className="inline-flex items-center gap-1 text-neutral-700">
              <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
              <strong className="text-neutral-900">{product.rating}</strong>
              <span className="text-neutral-500">/ 5.0</span>
            </span>
            <span className="text-neutral-300">|</span>
            <span className="text-neutral-700">
              SKU: <strong className="text-neutral-900">{product.sku}</strong>
            </span>
            <span className="text-neutral-300">|</span>
            <span className="text-neutral-700">
              Kapasitas: <strong className="text-neutral-900">{product.capacity}</strong>
            </span>
          </div>

          <div className="bg-neutral-50 rounded-2xl p-6 mb-7 border border-neutral-200">
            {product.price == null ? (
              <>
                <p className="text-xs text-neutral-500 mb-1">Harga</p>
                <div className="flex items-end gap-3">
                  <span
                    data-testid="product-detail-price"
                    className="font-display text-3xl md:text-4xl font-bold text-neutral-900"
                  >
                    Hubungi kami
                  </span>
                </div>
                <p className="text-sm text-neutral-500 mt-2">
                  Penawaran khusus tersedia untuk pembelian volume & proyek.
                </p>
              </>
            ) : (
              <>
                <p className="text-xs text-neutral-500 mb-1">Mulai dari</p>
                <div className="flex items-end gap-3">
                  <span
                    data-testid="product-detail-price"
                    className="font-display text-4xl font-bold text-neutral-900"
                  >
                    {priceLabel(product.price)}
                  </span>
                  <span className="text-sm text-neutral-500 mb-1">+PPN</span>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-wrap gap-3 mb-8">
            <button
              onClick={requestQuote}
              data-testid="request-quote-btn"
              className="inline-flex items-center gap-2 bg-px-red hover:bg-red-700 text-white font-semibold px-7 py-3.5 rounded-full transition-colors"
            >
              <Mail className="w-4 h-4" /> Minta Penawaran
            </button>
            <Link
              to="/contact"
              className="inline-flex items-center border border-neutral-300 hover:border-neutral-900 text-neutral-900 font-semibold px-7 py-3.5 rounded-full transition-colors"
            >
              Bicara dengan Engineer
            </Link>
          </div>

          <div className="grid grid-cols-3 gap-4 pt-6 border-t border-neutral-200">
            {[
              { icon: ShieldCheck, label: 'Garansi 2 Tahun' },
              { icon: Truck, label: 'Pengiriman Nasional' },
              { icon: Headphones, label: 'Service 24/7' },
            ].map((b, i) => (
              <div key={i} className="text-center">
                <b.icon className="w-6 h-6 text-px-red mx-auto mb-2" />
                <p className="text-xs text-neutral-700 font-medium">{b.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-t border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <div className="flex gap-2 mb-8 flex-wrap">
            {[
              { id: 'specs', label: 'Spesifikasi' },
              { id: 'features', label: 'Fitur Utama' },
              { id: 'support', label: 'Dukungan & Garansi' },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  tab === t.id
                    ? 'bg-neutral-900 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
                data-testid={`tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'specs' && (
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-3" data-testid="tab-content-specs">
              {Object.entries(product.specs).map(([k, v]) => (
                <div
                  key={k}
                  className="flex justify-between py-3 border-b border-neutral-200"
                >
                  <span className="text-sm text-neutral-600">{k}</span>
                  <span className="text-sm font-semibold text-neutral-900 text-right">
                    {String(v)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {tab === 'features' && (
            <ul className="space-y-4 max-w-2xl" data-testid="tab-content-features">
              {product.features.map((f, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <CheckCircle2 className="w-5 h-5 text-px-red flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-800">{f}</span>
                </li>
              ))}
            </ul>
          )}

          {tab === 'support' && (
            <div className="grid md:grid-cols-3 gap-5" data-testid="tab-content-support">
              {[
                {
                  t: 'Garansi Unit 2 Tahun',
                  d: 'Garansi penggantian unit untuk cacat produksi, berlaku nasional di seluruh service center resmi.',
                },
                {
                  t: 'Garansi Baterai 1 Tahun',
                  d: 'Garansi baterai internal 12 bulan; opsional upgrade ke baterai eksternal untuk runtime lebih panjang.',
                },
                {
                  t: 'Layanan Onsite',
                  d: 'Untuk seri industrial, tim engineer kami siap datang ke lokasi Anda 24/7.',
                },
              ].map((s, i) => (
                <div
                  key={i}
                  className="bg-neutral-50 rounded-2xl p-6 border border-neutral-200"
                >
                  <h4 className="font-display font-bold text-lg text-neutral-900 mb-2">
                    {s.t}
                  </h4>
                  <p className="text-sm text-neutral-600 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-neutral-50 section-pad">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
              MUNGKIN ANDA SUKA
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-neutral-900 mb-10">
              Produk terkait dari kategori yang sama
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/products/${p.id}`}
                  className="group relative bg-white rounded-2xl overflow-hidden hover-lift flex flex-col border border-neutral-200"
                >
                  {p.badge && (
                    <span className="absolute top-4 left-4 z-10 bg-px-red text-white text-[10px] font-bold tracking-wider px-2.5 py-1 rounded-full">
                      {p.badge}
                    </span>
                  )}
                  <div className="aspect-square bg-neutral-50 overflow-hidden flex items-center justify-center">
                    <img
                      src={absUrl(p.image)}
                      alt={p.name}
                      className="w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-base font-bold text-neutral-900 mb-1 line-clamp-2">
                      {p.name}
                    </h3>
                    <p className="text-xs text-neutral-500 mb-3">{p.capacity}</p>
                    <span className="text-sm font-bold text-px-red">
                      {priceLabel(p.price)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Full-screen zoom lightbox */}
      {lightboxOpen && (
        <div
          data-testid="image-lightbox"
          className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
          onClick={() => setLightboxOpen(false)}
        >
          <div
            className="flex items-center justify-between gap-4 px-5 py-4 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm font-medium truncate">{product.name}</p>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setZoomed((z) => !z)}
                data-testid="lightbox-zoom-toggle"
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 hover:bg-white/20 px-3.5 py-2 rounded-full transition-colors"
              >
                <ZoomIn className="w-4 h-4" />
                {zoomed ? 'Perkecil' : 'Zoom penuh'}
              </button>
              <button
                onClick={() => setLightboxOpen(false)}
                data-testid="lightbox-close"
                aria-label="Tutup"
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div
            className="flex-1 overflow-auto flex items-center justify-center p-4 select-none"
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          >
            <div className="relative">
              {isVideo(gallery[activeImg]) ? (
                <video
                  key={gallery[activeImg]}
                  src={gallery[activeImg]}
                  controls
                  playsInline
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  className="max-w-full max-h-[80vh]"
                  data-testid="lightbox-video"
                />
              ) : (
              <img
                src={absUrl(gallery[activeImg] || product.image)}
                alt={product.name}
                data-testid="lightbox-image"
                draggable={false}
                className={`pointer-events-none ${
                  zoomed
                    ? 'max-w-none w-auto h-auto'
                    : 'max-w-full max-h-full object-contain'
                }`}
                style={{ WebkitUserDrag: 'none', userSelect: 'none' }}
              />
              )}
              <span
                className="absolute bottom-3 left-3 z-10 text-xs font-bold tracking-[0.2em] text-white/80 bg-black/40 px-2 py-1 rounded pointer-events-none select-none"
                data-testid="lightbox-watermark"
              >
                powerx
              </span>
            </div>
          </div>

          {gallery.length > 1 && (
            <div
              className="flex items-center justify-center gap-4 pb-6"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() =>
                  setActiveImg((i) => (i - 1 + gallery.length) % gallery.length)
                }
                data-testid="lightbox-prev"
                aria-label="Sebelumnya"
                className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-white/80 text-sm">
                {activeImg + 1} / {gallery.length}
              </span>
              <button
                onClick={() => setActiveImg((i) => (i + 1) % gallery.length)}
                data-testid="lightbox-next"
                aria-label="Berikutnya"
                className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
